// File: src/components/EventPreview/PostReservationSuccessSheet.jsx

import React, {
  useEffect,
  useMemo,
  useState,
} from "react";
import {
  AnimatePresence,
  motion,
} from "framer-motion";

import { ReactComponent as TribeLogo } from "../../assets/TribeLogo.svg";

const TYPE_SPEED_MS = 38;
const DELETE_SPEED_MS = 20;
const CONFIRMATION_HOLD_MS = 900;
const BETWEEN_MESSAGES_MS = 180;
const FINAL_HOLD_MS = 2000;
const EXIT_DURATION_MS = 300;

function firstNonEmpty(...values) {
  return (
    values.find(
      (value) =>
        typeof value === "string" &&
        value.trim()
    )?.trim() || ""
  );
}

function getSegmentsLength(segments) {
  return segments.reduce(
    (total, segment) =>
      total + segment.text.length,
    0
  );
}

function renderVisibleSegments(
  visibleLength,
  segments
) {
  let remaining = visibleLength;

  return segments.map((segment, index) => {
    if (remaining <= 0) {
      return null;
    }

    const visibleText = segment.text.slice(
      0,
      remaining
    );

    remaining -= segment.text.length;

    return (
      <span
        key={`${segment.text}-${index}`}
        style={segment.style}
      >
        {visibleText}
      </span>
    );
  });
}

export default function PostReservationSuccessSheet({
  isOpen,
  gathering,
  onClose,
}) {
  const eventName = firstNonEmpty(
    gathering?.name,
    gathering?.title
  );

  const confirmationSegments = useMemo(
    () => [
      {
        text: "You reserved your spot for ",
        style: styles.textLight,
      },
      {
        text:
          eventName || "this gathering",
        style: styles.textStrong,
      },
      {
        text: "",
        style: styles.textLight,
      },
    ],
    [eventName]
  );

  const portalSegments = useMemo(
    () => [
      {
        text:
          "When the gathering starts, be at the listed address and tap ",
        style: styles.textLight,
      },
      {
        text: "Enter portal",
        style: styles.textStrong,
      },
      {
        text: "",
        style: styles.textLight,
      },
    ],
    []
  );

  const detailsSegments = useMemo(
    () => [
{
  text: "The portal gives you access to the ",
  style: styles.textLight,
},
{
  text: "group chat",
  style: styles.textStrong,
},
{
  text: " and ",
  style: styles.textLight,
},
{
  text: "exact meetup spot",
  style: styles.textStrong,
},
{
  text: " so you can easily find everyone.",
  style: styles.textLight,
},
    ],
    []
  );

  const confirmationLength = useMemo(
    () =>
      getSegmentsLength(
        confirmationSegments
      ),
    [confirmationSegments]
  );

  const portalLength = useMemo(
    () =>
      getSegmentsLength(
        portalSegments
      ),
    [portalSegments]
  );

  const detailsLength = useMemo(
    () =>
      getSegmentsLength(
        detailsSegments
      ),
    [detailsSegments]
  );

  const totalSequenceMs = useMemo(
    () =>
      confirmationLength *
        TYPE_SPEED_MS +
      CONFIRMATION_HOLD_MS +
      confirmationLength *
        DELETE_SPEED_MS +
      BETWEEN_MESSAGES_MS +
      portalLength *
        TYPE_SPEED_MS +
      CONFIRMATION_HOLD_MS +
      portalLength *
        DELETE_SPEED_MS +
      BETWEEN_MESSAGES_MS +
      detailsLength *
        TYPE_SPEED_MS +
      FINAL_HOLD_MS,
    [
      confirmationLength,
      portalLength,
      detailsLength,
    ]
  );

  const [
    activeMessage,
    setActiveMessage,
  ] = useState("confirmation");

  const [
    visibleLength,
    setVisibleLength,
  ] = useState(0);

  const [phase, setPhase] =
    useState("typing");

  const activeSegments =
    activeMessage === "confirmation"
      ? confirmationSegments
      : activeMessage === "portal"
        ? portalSegments
        : detailsSegments;

  useEffect(() => {
    if (!isOpen) {
      return undefined;
    }

    setActiveMessage("confirmation");
    setVisibleLength(0);
    setPhase("typing");

    const previousBodyOverflow =
      document.body.style.overflow;

    const previousHtmlOverflow =
      document.documentElement.style
        .overflow;

    document.body.style.overflow =
      "hidden";

    document.documentElement.style.overflow =
      "hidden";

    const handleKeyDown = (event) => {
      if (event.key === "Escape") {
        onClose?.();
      }
    };

    window.addEventListener(
      "keydown",
      handleKeyDown
    );

    return () => {
      window.removeEventListener(
        "keydown",
        handleKeyDown
      );

      document.body.style.overflow =
        previousBodyOverflow;

      document.documentElement.style.overflow =
        previousHtmlOverflow;
    };
  }, [
    isOpen,
    eventName,
    onClose,
  ]);

  useEffect(() => {
    if (!isOpen) {
      return undefined;
    }

    const activeLength =
      getSegmentsLength(activeSegments);

    if (phase === "typing") {
      if (
        visibleLength >= activeLength
      ) {
        const holdDuration =
          activeMessage === "details"
            ? FINAL_HOLD_MS
            : CONFIRMATION_HOLD_MS;

        const timer =
          window.setTimeout(() => {
            if (
              activeMessage === "details"
            ) {
              setPhase("complete");
            } else {
              setPhase("deleting");
            }
          }, holdDuration);

        return () => {
          window.clearTimeout(timer);
        };
      }

      const timer =
        window.setTimeout(() => {
          setVisibleLength(
            (current) =>
              Math.min(
                activeLength,
                current + 1
              )
          );
        }, TYPE_SPEED_MS);

      return () => {
        window.clearTimeout(timer);
      };
    }

    if (phase === "deleting") {
      if (visibleLength <= 0) {
        const timer =
          window.setTimeout(() => {
            setActiveMessage(
              activeMessage ===
                "confirmation"
                ? "portal"
                : "details"
            );

            setVisibleLength(0);
            setPhase("typing");
          }, BETWEEN_MESSAGES_MS);

        return () => {
          window.clearTimeout(timer);
        };
      }

      const timer =
        window.setTimeout(() => {
          setVisibleLength(
            (current) =>
              Math.max(
                0,
                current - 1
              )
          );
        }, DELETE_SPEED_MS);

      return () => {
        window.clearTimeout(timer);
      };
    }

    if (phase === "complete") {
      onClose?.();
    }

    return undefined;
  }, [
    isOpen,
    phase,
    visibleLength,
    activeMessage,
    activeSegments,
    onClose,
  ]);

  return (
    <AnimatePresence>
      {isOpen ? (
        <motion.section
          key="post-reservation-success"
          role="dialog"
          aria-modal="true"
          aria-label="Reservation confirmed"
          initial={{
            opacity: 0,
          }}
          animate={{
            opacity: 1,
          }}
          exit={{
            opacity: 0,
          }}
          transition={{
            duration:
              EXIT_DURATION_MS / 1000,
          }}
          style={styles.shell}
        >
          <motion.div
            initial={{
              opacity: 0,
              y: -8,
            }}
            animate={{
              opacity: 1,
              y: 0,
            }}
            transition={{
              duration: 0.35,
              ease: [
                0.22,
                1,
                0.36,
                1,
              ],
            }}
            style={styles.logoWrap}
          >
            <TribeLogo
              style={styles.logo}
            />
          </motion.div>

          <main style={styles.main}>
            <h1 style={styles.message}>
              {renderVisibleSegments(
                visibleLength,
                activeSegments
              )}

              <span
                aria-hidden="true"
                style={styles.cursor}
              >
                |
              </span>
            </h1>

    <div
  aria-hidden="true"
  style={styles.progressTrack}
>
  <motion.div
    key={`${eventName}-${isOpen}`}
    initial={{
      scaleX: 0,
    }}
    animate={{
      scaleX: 1,
    }}
    transition={{
      duration:
        totalSequenceMs /
        1000,
      ease: "linear",
    }}
    style={styles.progressFill}
  />
</div>

<button
  type="button"
  onClick={onClose}
  style={styles.skipButton}
>
  Skip
</button>
</main>
          <style>
            {`
              @keyframes reservationSuccessCursor {
                0%,
                45% {
                  opacity: 1;
                }

                46%,
                100% {
                  opacity: 0;
                }
              }

              @media (prefers-reduced-motion: reduce) {
                * {
                  animation: none !important;
                  transition: none !important;
                }
              }
            `}
          </style>
        </motion.section>
      ) : null}
    </AnimatePresence>
  );
}

const styles = {
  shell: {
    position: "fixed",
    inset: 0,
    zIndex: 10020,
    width: "100%",
    height: "100dvh",
    overflow: "hidden",
    boxSizing: "border-box",
    background: "#ffffff",
    color: "#0b0b0b",
    fontFamily:
      "'Inter', system-ui, -apple-system, BlinkMacSystemFont, 'Segoe UI', sans-serif",
  },

  logoWrap: {
    position: "absolute",
    top:
      "calc(20px + env(safe-area-inset-top, 0px))",
    left: "40%",
    zIndex: 2,
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    transform: "translateX(-50%)",
    pointerEvents: "none",
  },

  logo: {
    width: 94,
    height: "auto",
    display: "block",
  },

  main: {
    width: "100%",
    height: "100%",
    padding:
      "calc(0px + env(safe-area-inset-top, 0px)) 24px calc(32px + env(safe-area-inset-bottom, 0px))",
    display: "flex",
    flexDirection: "column",
    alignItems: "center",
    justifyContent: "center",
    transform: "translateY(-20px)",
    boxSizing: "border-box",
    textAlign: "center",
  },

  message: {
    width: "100%",
    maxWidth: 820,
    minHeight: "2.15em",
    margin: 0,
    color: "#292929",
    fontSize:
      "clamp(1.9rem, 7.5vw, 5rem)",
    lineHeight: 1.04,
    fontWeight: 520,
    letterSpacing: "-0.055em",
  },

  textLight: {
    color: "#292929",
    fontWeight: 320,
  },

  textStrong: {
    color: "#0b0b0b",
    fontWeight: 760,
  },

  cursor: {
    display: "inline-block",
    marginLeft: 3,
    color: "#0b0b0b",
    fontWeight: 400,
    animation:
      "reservationSuccessCursor 0.8s steps(1) infinite",
  },

  progressTrack: {
    width: 52,
    height: 3,
    marginTop: 30,
    overflow: "hidden",
    borderRadius: 999,
    background:
      "rgba(17,17,17,0.1)",
  },

  progressFill: {
    width: "100%",
    height: "100%",
    borderRadius: 999,
    background: "#111111",
    transformOrigin: "left center",
  },
  skipButton: {
  display: "block",
  margin: "15px auto 0",
  padding: "10px 14px",
  border: "none",
  borderRadius: 11,
  background: "transparent",
  color: "#666",
  fontSize: 12.8,
  lineHeight: 1,
  fontWeight: 700,
  letterSpacing: "-0.015em",
  cursor: "pointer",
  WebkitTapHighlightColor: "transparent",
},
};